import React, { createContext, useContext, type ReactNode } from 'react';
import { useColorScheme } from 'react-native';
import { lightTheme, darkTheme, type Theme } from '../constants/theme';

type ThemeMode = 'light' | 'dark' | 'auto';

export interface ThemeConfig {
  mode?: ThemeMode;
  lightTheme?: Partial<Theme>;
  darkTheme?: Partial<Theme>;
}

interface ThemeContextValue {
  theme: Theme;
  colorScheme: 'light' | 'dark';
  mode: ThemeMode;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

interface ThemeProviderProps {
  children: ReactNode;
  config?: ThemeConfig;
}

export function ThemeProvider({ children, config = {} }: ThemeProviderProps) {
  const systemColorScheme = useColorScheme() || 'light';
  const mode = config.mode || 'auto';

  // Determine active color scheme
  const colorScheme: 'light' | 'dark' =
    mode === 'auto' ? systemColorScheme : mode;

  // Merge user's custom theme with defaults
  const customLightTheme = config.lightTheme
    ? { ...lightTheme, ...config.lightTheme }
    : lightTheme;

  const customDarkTheme = config.darkTheme
    ? { ...darkTheme, ...config.darkTheme }
    : darkTheme;

  const theme = colorScheme === 'dark' ? customDarkTheme : customLightTheme;

  const value: ThemeContextValue = {
    theme,
    colorScheme,
    mode,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const systemColorScheme = useColorScheme() || 'light';
  const context = useContext(ThemeContext);

  if (!context) {
    // Fallback to default theme if provider is not used
    return {
      theme: systemColorScheme === 'dark' ? darkTheme : lightTheme,
      colorScheme: systemColorScheme,
      mode: 'auto',
    };
  }

  return context;
}
